import type { BusinessInfo, Dependent, Sex, TeamMember, WaiveReason } from "./types";

/** US states + DC for the address state select (value = USPS code). */
export const US_STATES: { value: string; label: string }[] = [
  { value: "AL", label: "Alabama" },
  { value: "AK", label: "Alaska" },
  { value: "AZ", label: "Arizona" },
  { value: "AR", label: "Arkansas" },
  { value: "CA", label: "California" },
  { value: "CO", label: "Colorado" },
  { value: "CT", label: "Connecticut" },
  { value: "DE", label: "Delaware" },
  { value: "DC", label: "District of Columbia" },
  { value: "FL", label: "Florida" },
  { value: "GA", label: "Georgia" },
  { value: "HI", label: "Hawaii" },
  { value: "ID", label: "Idaho" },
  { value: "IL", label: "Illinois" },
  { value: "IN", label: "Indiana" },
  { value: "IA", label: "Iowa" },
  { value: "KS", label: "Kansas" },
  { value: "KY", label: "Kentucky" },
  { value: "LA", label: "Louisiana" },
  { value: "ME", label: "Maine" },
  { value: "MD", label: "Maryland" },
  { value: "MA", label: "Massachusetts" },
  { value: "MI", label: "Michigan" },
  { value: "MN", label: "Minnesota" },
  { value: "MS", label: "Mississippi" },
  { value: "MO", label: "Missouri" },
  { value: "MT", label: "Montana" },
  { value: "NE", label: "Nebraska" },
  { value: "NV", label: "Nevada" },
  { value: "NH", label: "New Hampshire" },
  { value: "NJ", label: "New Jersey" },
  { value: "NM", label: "New Mexico" },
  { value: "NY", label: "New York" },
  { value: "NC", label: "North Carolina" },
  { value: "ND", label: "North Dakota" },
  { value: "OH", label: "Ohio" },
  { value: "OK", label: "Oklahoma" },
  { value: "OR", label: "Oregon" },
  { value: "PA", label: "Pennsylvania" },
  { value: "RI", label: "Rhode Island" },
  { value: "SC", label: "South Carolina" },
  { value: "SD", label: "South Dakota" },
  { value: "TN", label: "Tennessee" },
  { value: "TX", label: "Texas" },
  { value: "UT", label: "Utah" },
  { value: "VT", label: "Vermont" },
  { value: "VA", label: "Virginia" },
  { value: "WA", label: "Washington" },
  { value: "WV", label: "West Virginia" },
  { value: "WI", label: "Wisconsin" },
  { value: "WY", label: "Wyoming" },
];

const STATE_CODES = new Set(US_STATES.map((s) => s.value));
const STATE_CODE_BY_LABEL = new Map(US_STATES.map((s) => [s.label.toLowerCase(), s.value]));

/**
 * Normalize a state value to its USPS code (what the state `<Select>` keys on).
 * Google Places returns the full state name (e.g. "New York") while the select
 * options are 2-letter codes, so an unnormalized value never matches and the
 * field silently stays blank. Accepts either a code or a full name; returns ""
 * when it can't be resolved so the user can pick manually.
 */
export function toStateCode(input: string | null | undefined): string {
  const value = (input ?? "").trim();
  if (!value) return "";
  const upper = value.toUpperCase();
  if (upper.length === 2 && STATE_CODES.has(upper)) return upper;
  return STATE_CODE_BY_LABEL.get(value.toLowerCase()) ?? "";
}

export const SEX_OPTIONS: { value: Sex; label: string }[] = [
  { value: "female", label: "Female" },
  { value: "male", label: "Male" },
];

/** Waive reasons offered when a member is marked "Not enrolling" (mirrors the employee-link flow). */
export const WAIVE_REASONS: { value: WaiveReason; label: string }[] = [
  { value: "spouse-plan", label: "Covered by a spouse or partner's plan" },
  { value: "parent-plan", label: "Covered by a parent's plan" },
  { value: "marketplace", label: "Has their own individual / Marketplace plan" },
  { value: "government", label: "Has Medicare, Medicaid, or VA coverage" },
  { value: "declining", label: "Doesn't want coverage right now" },
  { value: "other", label: "Another reason" },
];

let memberSeq = 0;
export function makeTeamMember(overrides: Partial<TeamMember> = {}): TeamMember {
  memberSeq += 1;
  return {
    id: `cc-member-${memberSeq}`,
    firstName: "",
    lastName: "",
    sex: "",
    zip: "",
    dateOfBirth: "",
    dependentMethod: null,
    dependents: [],
    ...overrides,
  };
}

let dependentSeq = 0;
export function makeDependent(
  relationship: Dependent["relationship"],
  overrides: Partial<Dependent> = {},
): Dependent {
  dependentSeq += 1;
  return {
    id: `cc-dep-${dependentSeq}`,
    relationship,
    firstName: "",
    lastName: "",
    dateOfBirth: "",
    sex: "",
    zip: "",
    ...overrides,
  };
}

/** Today as a YYYY-MM-DD value, for date `max` bounds. */
export function todayInputValue(): string {
  return new Date().toISOString().slice(0, 10);
}

/** True for a plausible date of birth (1900-01-01 … today). */
export function validDateOfBirth(value: string): boolean {
  if (!value) return false;
  const date = new Date(`${value}T00:00:00`);
  return !Number.isNaN(date.getTime()) && value >= "1900-01-01" && value <= todayInputValue();
}

// Accept a 5-digit ZIP or a 9-digit ZIP+4 in "xxxxx-xxxx" form.
const ZIP_RE = /^\d{5}(-\d{4})?$/;
export function validZip(value: string): boolean {
  return ZIP_RE.test(value.trim());
}

/**
 * Format ZIP keystrokes into "12345" or "12345-6789": keep up to 9 digits and
 * insert the ZIP+4 hyphen after the fifth. Use in every ZIP field's `onChange`
 * so the display always stays in the canonical `xxxxx-xxxx` shape.
 */
export function formatZip(value: string): string {
  const digits = value.replace(/\D/g, "").slice(0, 9);
  return digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
}

/** EIN is nine digits, formatted `XX-XXXXXXX` (mirrors the API + DB constraint). */
const EIN_RE = /^\d{2}-?\d{7}$/;

/** A complete EIN once it has nine digits (dash optional). */
export function validEin(value: string): boolean {
  return EIN_RE.test(value.trim());
}

/**
 * Format EIN keystrokes into "XX-XXXXXXX": keep up to nine digits and insert the
 * hyphen after the second. Use in the EIN field's `onChange` so the display
 * stays canonical.
 */
export function formatEin(value: string): string {
  const digits = value.replace(/\D/g, "").slice(0, 9);
  return digits.length > 2 ? `${digits.slice(0, 2)}-${digits.slice(2)}` : digits;
}

/** A team member is complete once we have all four underwriting basics. */
export function teamMemberComplete(member: TeamMember): boolean {
  return (
    member.firstName.trim().length > 0 &&
    member.lastName.trim().length > 0 &&
    member.sex.length > 0 &&
    validZip(member.zip) &&
    validDateOfBirth(member.dateOfBirth)
  );
}

/**
 * The field labels a member is still missing, in the order the card renders
 * them. Empty exactly when `teamMemberComplete` is true — the two are kept in
 * step by `data.test.ts`.
 *
 * This exists because "Open" on its own is a dead end. A payroll pull routinely
 * lands 40 people of whom three lack a birth date, and an employer told only
 * that three are "Open" has to hunt through the accordion to find out which
 * three and what each one needs. Naming the gap turns that hunt into a click.
 */
export function teamMemberMissingFields(member: TeamMember): string[] {
  const missing: string[] = [];
  if (member.firstName.trim().length === 0) missing.push("First name");
  if (member.lastName.trim().length === 0) missing.push("Last name");
  if (member.sex.length === 0) missing.push("Sex");
  if (!validZip(member.zip)) missing.push("Home ZIP");
  if (!validDateOfBirth(member.dateOfBirth)) missing.push("Date of birth");
  return missing;
}

/**
 * Fold a fresh HR-system pull into the roster already on screen.
 *
 * Re-importing used to replace the roster outright, which quietly threw away
 * every correction the employer had made — the failure a customer hit after
 * editing 28 people, going back, and reconnecting.
 *
 * The rules, in order:
 *  - A pulled person we already have keeps the row that is on screen, including
 *    its id, so React state, the open accordion card and every edit survive. We
 *    do fill BLANK fields from the pull: if the employer fixed something in
 *    QuickBooks and re-imported, that value should land, while anything they
 *    typed here outranks the provider.
 *  - A pulled person we don't have is appended.
 *  - A person on screen who is NOT in the pull is kept. They were either added
 *    by hand or have since left; dropping them silently is the same class of
 *    bug we're fixing, and removing someone is one click.
 *  - Untouched blank starter rows (the single empty member the flow seeds) are
 *    dropped, so a first import doesn't leave an empty card at the top.
 *
 * Matching is by the provider's `externalId` where we have one, and falls back
 * to name + date of birth where we don't. The fallback is NOT optional: the
 * census draft does not persist `externalId`, so everyone restored from a saved
 * draft the next day arrives without one. On `externalId` alone, re-importing
 * in a later session would match nobody and append the entire roster a second
 * time — 44 people becoming 88. A matched row adopts the id, so subsequent
 * merges in that session are exact.
 */
export function mergePulledMembers(existing: TeamMember[], pulled: TeamMember[]): TeamMember[] {
  const isBlankStarter = (m: TeamMember) =>
    !m.externalId &&
    m.firstName.trim() === "" &&
    m.lastName.trim() === "" &&
    m.sex === "" &&
    m.zip === "" &&
    m.dateOfBirth === "";

  /** Name + DOB, normalized. Empty when there isn't enough to identify anyone. */
  const naturalKey = (m: TeamMember): string => {
    const first = m.firstName.trim().toLowerCase();
    const last = m.lastName.trim().toLowerCase();
    if (!first && !last) return "";
    return `${first}|${last}|${m.dateOfBirth.trim()}`;
  };

  const kept = existing.filter((m) => !isBlankStarter(m));
  // Consumed so one pulled person can never be merged onto two rows — two
  // genuine namesakes with no DOB would otherwise collapse into one.
  const consumed = new Set<TeamMember>();

  const findMatch = (m: TeamMember): TeamMember | undefined => {
    if (m.externalId) {
      const byId = pulled.find((p) => p.externalId === m.externalId && !consumed.has(p));
      if (byId) return byId;
      // An id we hold that the pull no longer lists means they've left; don't
      // fall back to the name, or a namesake would silently inherit their row.
      return undefined;
    }
    const key = naturalKey(m);
    if (!key) return undefined;
    return pulled.find((p) => naturalKey(p) === key && !consumed.has(p));
  };

  const merged = kept.map((m) => {
    const match = findMatch(m);
    if (!match) return m;
    consumed.add(match);
    // Employer-entered values win; the pull only fills gaps.
    return {
      ...m,
      externalId: m.externalId ?? match.externalId,
      firstName: m.firstName.trim() || match.firstName,
      lastName: m.lastName.trim() || match.lastName,
      sex: m.sex || match.sex,
      zip: m.zip.trim() || match.zip,
      dateOfBirth: m.dateOfBirth.trim() || match.dateOfBirth,
    };
  });

  const added = pulled.filter((p) => !consumed.has(p));
  return [...merged, ...added];
}

/**
 * A dependent row is complete once it has a name, DOB, and sex. ZIP is only
 * required when the member's "same ZIP" toggle is off (`sameZip === false`);
 * otherwise the member's ZIP is applied at submit.
 */
export function dependentComplete(dependent: Dependent, sameZip = true): boolean {
  return (
    dependent.firstName.trim().length > 0 &&
    dependent.lastName.trim().length > 0 &&
    validDateOfBirth(dependent.dateOfBirth) &&
    dependent.sex.length > 0 &&
    (sameZip || validZip(dependent.zip))
  );
}

/**
 * The business step is complete once we have the name, EIN, and a full mailing
 * address (street, city, state, valid ZIP). Phone is the ONLY optional field —
 * everything else is mandatory before the flow can advance.
 */
export function businessComplete(business: BusinessInfo): boolean {
  return (
    business.name.trim().length > 0 &&
    validEin(business.ein) &&
    business.address.trim().length > 0 &&
    business.city.trim().length > 0 &&
    business.state.trim().length > 0 &&
    validZip(business.zip)
  );
}

/**
 * Every dependent a member has added must be fully filled in before submit: a
 * dependent row is optional, but once it's there it's mandatory (the employer
 * can always remove it instead). Waived (non-enrolling) members carry no
 * dependents — but waiving is complete only once they tell us why (mirrors the
 * employee-link flow).
 */
export function memberDependentsComplete(member: TeamMember): boolean {
  if (member.enrolling === false) return Boolean(member.notEnrollingReason);
  const sameZip = member.dependentsSameZip !== false;
  return member.dependents.every((dependent) => dependentComplete(dependent, sameZip));
}

/** The business profile an HR/payroll pull returns: every field may be absent. */
export interface PulledBusiness {
  name: string;
  address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  phone: string | null;
  /** Only QuickBooks supplies one; BambooHR's company endpoint has no EIN. */
  ein?: string | null;
}

/**
 * Prefill the business profile from a pull, one field at a time.
 *
 * The business step comes BEFORE the roster step and requires every field but
 * phone, so by the time a pull lands the employer has usually typed most of
 * this. The rule is the platform's company-facts rule (cakewalk-platform ADR
 * 0040 D14): fill a field only while it is blank, never overwrite what the
 * employer typed. It replaces an all-or-nothing check that skipped the whole
 * profile as soon as a name existed, which kept the pull from ever filling a
 * blank phone or EIN.
 */
export function mergePulledBusiness(prev: BusinessInfo, pulled: PulledBusiness): BusinessInfo {
  const fill = (current: string, next: string | null | undefined): string =>
    current.trim() ? current : (next ?? "").trim();
  return {
    name: fill(prev.name, pulled.name),
    ein: fill(prev.ein, pulled.ein ? formatEin(pulled.ein) : null),
    address: fill(prev.address, pulled.address),
    city: fill(prev.city, pulled.city),
    state: fill(prev.state, toStateCode(pulled.state) || pulled.state),
    zip: fill(prev.zip, pulled.zip),
    phone: fill(prev.phone, pulled.phone),
  };
}

/**
 * A warning when the EIN the employer typed differs from the one their payroll
 * system has on file, or null when they agree or either side is missing.
 *
 * The other half of D14: a disagreement is flagged, not silently resolved. EIN
 * is the one business field where a mismatch is unambiguous (nine digits, no
 * abbreviations to normalize) and costly: a carrier rejects a group whose EIN is
 * wrong.
 */
export function einMismatchWarning(
  typed: string,
  pulled: string | null | undefined,
  providerName: string,
): string | null {
  const typedDigits = typed.replace(/\D/g, "");
  const pulledDigits = (pulled ?? "").replace(/\D/g, "");
  if (typedDigits.length !== 9 || pulledDigits.length !== 9) return null;
  if (typedDigits === pulledDigits) return null;
  return (
    `${providerName} lists your company's EIN as ${formatEin(pulledDigits)}, but you entered ` +
    `${formatEin(typedDigits)}. Go back to the business step if yours needs correcting.`
  );
}
